import {
  AfterViewInit,
  Component,
  effect,
  EventEmitter,
  Inject,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { ProductSelectorComponent } from "../product.selector.component";
import { intersection } from "../../helpers";
import { Subject, Subscription } from "rxjs";
import { CartService } from "../../services/cart.service";
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { PageService } from '@gea-waldviertler-website/shop-lib';
import {
  AttributeSelectionsInterface,
  DistinctSimpleProductInterface,
  ProductAttributeInterface,
  ProductAttributeOptionInterface,
  ProductAttributesOptionInterface,
  ProductVariantInterface,
  SimpleProduct,
  SimpleProductVariantInterface
} from "@gea-waldviertler-website/gea-models";

@Component({
    selector: 'gea-shop-simple-configurator',
    templateUrl: './simple.component.html',
    styleUrls: [],
    standalone: false
})
export class SimpleConfiguratorComponent extends ProductSelectorComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit
{
  loadedProduct = input.required<SimpleProduct>()
  @Input() resetSelectionSubject?: Subject<boolean>
  @Input() distinctRefreshSubject?: Subject<void>
  @Output("activeVariantChange") setVariantEvent = new EventEmitter<ProductVariantInterface>()
  @Output("distinctProductSelected") distinctProductSelectedEvent = new EventEmitter<DistinctSimpleProductInterface | undefined>()
  @Output("priceChanged") priceChangedEvent = new EventEmitter<number>()
  @Output("currentSelectionChange") currentSelectionChangeEvent = new EventEmitter<AttributeSelectionsInterface>()
  
  screenService: ScreenSizeService

  private subscriptions: Subscription[] = []

  currentSelections: ProductAttributesOptionInterface = {}
  validating: boolean = false
  
  storePickupEnabled: boolean = true

  preselection = input<string[][]>([])

  constructor(
    @Inject(CartService) private cartService: CartService,
    @Inject(PageService) private pageService: PageService,
    @Inject(ScreenSizeService) screenSizeService: ScreenSizeService,
  ) {
    super()
    this.screenService = screenSizeService

    this.pageService.getConfigSubject().subscribe(config => {
      if (config) this.storePickupEnabled = config.storePickupEnabled
    })
    
    effect(() => {
      const ps = this.preselection()
      this.setPreselection(ps)
    })

    effect(() => {
      const swatchAttribute = this.loadedProduct().getSwatchAttribute()
      // if a preselection (url hash) is present, let setPreselection() pick the swatch
      if (this.preselection().length) {
        return
      }
      if (swatchAttribute) {
        // do not re-toggle if the swatch already has a selection
        if (this.currentSelections.hasOwnProperty(swatchAttribute.code)) {
          return
        }
        const firstOption = swatchAttribute.options[0]
        if (firstOption) {
          this.toggleActiveSelection(swatchAttribute.code, firstOption)
        }
      }
    })
  }

  ngOnInit() {
    const resetSub = this.resetSelectionSubject?.subscribe(value => {
      if (value) {
        this.clearSelections()
      }
    })
    if (resetSub) this.subscriptions.push(resetSub)

    // Re-resolve + re-emit the distinct product on demand. On live, the active shop event ("Aktion")
    // arrives asynchronously after the initial render, so ProductService re-emits the product page for
    // the SAME instance. Because this swatch-less/one-shot configurator only emits in ngOnInit, that
    // re-emit would otherwise leave `distinctProduct` undefined. Re-running the resolution keeps the
    // order tiles + add-to-cart button alive and refreshes the Aktion price/badge.
    const refreshSub = this.distinctRefreshSubject?.subscribe(() => {
      this.resolveDistinctProduct()
    })
    if (refreshSub) this.subscriptions.push(refreshSub)

    // if exact one variant is found, fire setVariant Event
    let variants = this.getPossibleVariants()
    if (variants.length == 1) {
      this.setVariantEvent.emit(variants[0])
      this.handleDistinct()
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.subscriptions = []
  }

  ngAfterViewInit() {
    
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  /**
   * Re-runs the distinct-product resolution from the current selections and re-emits it. Mirrors the
   * ngOnInit / toggleActiveSelection resolution so an asynchronous product-page re-emit (e.g. an
   * active Aktion arriving late) can never leave `distinctProduct` blank for a resolvable product.
   */
  private resolveDistinctProduct() {
    let selections: AttributeSelectionsInterface = {}
    for (const pair of Object.entries(this.currentSelections)) {
      selections[pair[0]] = { attributeValues: pair[1].optionIds, attributeCode: pair[0] }
    }

    const variant = this.loadedProduct().getSelectedVariant(selections)
    if (variant) {
      this.setVariantEvent.emit(variant)
    } else {
      let variants = this.getPossibleVariants()
      if (variants.length == 1) {
        this.setVariantEvent.emit(variants[0])
      }
    }

    this.handleDistinct()
  }

  clearSelections() {
    this.currentSelections = {}
  }

  toggleActiveSelection(
    attributeCode?: string,
    option?: ProductAttributeOptionInterface | null,
    throwEvent = true,
  ): boolean {

    if (! attributeCode || ! option) {
      return false
    }

    // do nothing if selection does not exist
    if (! this.isSelectionPossible(attributeCode, option)) {
      return false
    }
    if (! this.isSelectionInStockOrReminder(attributeCode, option)) {
      return false
    }

    // delete selection (deselect attribute selection) — but never deselect the swatch attribute
    if (this.currentSelections.hasOwnProperty(attributeCode)
        && intersection(option.optionIds, this.currentSelections[attributeCode].optionIds).length
        && this.loadedProduct().getSwatchAttribute()?.code != attributeCode) {
      delete this.currentSelections[attributeCode]
    } else {
      // set attribute selection
      this.currentSelections[attributeCode] = option

    }

    let selections: AttributeSelectionsInterface = {}
    for (const pair of Object.entries(this.currentSelections)) {
      selections[pair[0]] = { attributeValues: pair[1].optionIds, attributeCode: pair[0] }
    }
    if (throwEvent) this.currentSelectionChangeEvent.emit(selections)

    this.handleDistinct()

    const variant = this.loadedProduct().getSelectedVariant(selections)
    if (variant) {
      this.setVariantEvent.emit(variant)
    } else {
      let variants = this.getPossibleVariants()
      if (variants.length == 1) {
        this.setVariantEvent.emit(variants[0])
      }
    }


    return true
  }

  handleDistinct() {
    const distinctProduct = this.getDistinctProduct()
    if (! distinctProduct) {
      this.distinctProductSelectedEvent.emit(undefined)
      return
    }

    this.distinctProductSelectedEvent.emit(distinctProduct)
    this.priceChangedEvent.emit(distinctProduct.price)
  }

  isSwatchOption(attribute: ProductAttributeInterface) {
    return this.loadedProduct()?.getSwatchAttribute()?.code == attribute.code
  }

  isSelected(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean {
    if (! attributeCode || !option) {
      return false
    }

    if (this.currentSelections.hasOwnProperty(attributeCode)) {
      if (intersection(option.optionIds, this.currentSelections[attributeCode].optionIds).length) {
        return true
      }
    }

    return false
  }

  isSelectionPossible(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean | number {

    let isPossible = false

    let variants = this.getPossibleVariants(attributeCode, option, true)

    if (variants.length > 0) {
      isPossible = true
    }

    return isPossible
  }

  getTooltip(attribute: ProductAttributeInterface, option: ProductAttributeOptionInterface): string | null {
    if(! this.isSelectionPossible(attribute.code, option)) {
      return 'Die ' + attribute.title + ' ' + option.title + ' wird aktuell in der gewählten Kombination nicht angeboten.'
    }
    if(! this.isSelectionInStockOrReminder(attribute.code, option)) {
      return 'Die Auswahl ist aktuell nicht verfügbar.'
    }

    return null
  }

  isSelectionInStockOrReminder(attributeCode?: string, option?: ProductAttributeOptionInterface): boolean {
    if (! this.loadedProduct() || ! attributeCode || ! option) {
      return false
    }

    const attribute = this.loadedProduct().selectableAttributes.get(attributeCode)
    if(! attribute) return false

    if(attribute.scope == 'V') {
      for (let i = 0; i < this.loadedProduct().variants.length; i++) {
        const variant = this.loadedProduct().variants[i]
        const optionId = variant.attributes.get(attributeCode)

        if(! optionId || ! option.optionIds.includes(optionId)) {
          continue
        }

        if(this.loadedProduct().canBeOrdered(this.storePickupEnabled, 1, variant.variantId) || this.loadedProduct().isRemindPossible(variant)) {
          return true
        }
      }

      return false
    }

    return false
  }

  getPossibleVariants(attributeCode?: string, option?: ProductAttributeOptionInterface, skipOwn?: boolean):
    ProductVariantInterface[] {
    let v: SimpleProductVariantInterface[] = []
    if (! this.loadedProduct()) return v
    this.loadedProduct().variants.forEach((variant, index) => {
      let includeVariant = true

      // if params are set, include in set
      if (option && attributeCode) {
        variant = variant as SimpleProductVariantInterface
        let atc = variant.attributes.get(attributeCode)
        if (!(atc && option.optionIds.includes(atc))) {
          includeVariant = false
        }
      }

      // check currentSelections
      Object.entries(this.currentSelections).forEach(([key, attributeOption]) => {

        if (skipOwn === true && attributeCode) {
          if (key == attributeCode) return
        }

        let atc = variant.attributes.get(key)
        if (!(atc && attributeOption.optionIds.includes(atc))) {
          includeVariant = false
        }
      })

      if (includeVariant) {
        v.push(variant)
      }
    })

    return v
  }

  getDistinctProduct(): DistinctSimpleProductInterface | undefined {

    let variants = this.getPossibleVariants()

    if (variants.length != 1) {
      return undefined
    }

    const simpleVariant = variants[0] as SimpleProductVariantInterface

    return {
      product: this.loadedProduct(),
      selectedVariant: simpleVariant.variantId,
      price: simpleVariant.price,
      priceSpecial: simpleVariant.specialPriceFrom ? simpleVariant.specialPriceFrom : undefined,
      qualityId: simpleVariant.qualityId
    }
  }

  // this method is called after stock has loaded in product.component
  private setPreselection(keyValues: string[][]) {
    if (! keyValues.length) {
      return
    }

    const cart = this.cartService.cart()
    if (keyValues[0][0] == 'cartItem' && cart.items.length > 0) {
      // test if called product by cartitem in checkout
      const cartItemId = parseInt(keyValues[0][1])
      const cartItem = cart?.items[cartItemId]
      if (cartItem && cartItem.distinctProduct.product && cartItem.distinctProduct.product.selectableAttributes) {
        cartItem.distinctProduct.product?.variants.forEach((variant: ProductVariantInterface,) => {
          if (variant.variantId == cartItem.distinctProduct.selectedVariant) {
            for (const attKV of cartItem!.distinctProduct.product!.selectableAttributes.entries()) {
              const attributeCode = attKV[0]
              const attribute = attKV[1]
              for (const option of attribute.options) {
                const variantOptionId = variant.attributes.get(attributeCode)
                if (! variantOptionId) continue
                if (option.optionIds.includes(variantOptionId)) {
                  this.toggleActiveSelection(attributeCode, option)
                }
              }
            }
          }
        })
      }
    } else {
      // generic url-hash preselection (e.g. #farbe=<id> from the overview/category page)
      this.preselectAttributes(keyValues)
    }
  }

  // applies a generic [[code, id]] hash preselection idempotently, preferring the
  // hash-requested swatch and only falling back to the first orderable swatch
  private preselectAttributes(keyValues: string[][]) {
    const swatchCode = this.loadedProduct().getSwatchAttribute()?.code
    let swatchAttributeSelected = false
    // remember the swatch option requested via the url hash
    let requestedSwatchOption: ProductAttributeOptionInterface | undefined

    for (let i = 0; i < keyValues.length; i++) {
      const code = keyValues[i][0]
      const options = this.loadedProduct().selectableAttributes.get(code)?.options
      if (options) {
        for (const option of options) {
          if (option.optionIds.includes(+keyValues[i][1])) {
            if (swatchCode == code) {
              requestedSwatchOption = option
            }
            // idempotent: only toggle if this attribute is not already resolved to the option
            if (this.isSelected(code, option)) {
              if (swatchCode == code) swatchAttributeSelected = true
            } else {
              const selected = this.toggleActiveSelection(code, option, false)
              if (selected && swatchCode == code) {
                swatchAttributeSelected = true
              }
            }
          }
        }
      }
    }

    // if no swatch attribute has been selected, prefer the hash-requested swatch
    // before falling back to the first orderable swatch (options[0])
    if (! swatchAttributeSelected) {
      if (requestedSwatchOption && this.toggleActiveSelection(swatchCode, requestedSwatchOption, false)) {
        return
      }
      this.selectFirstSwatch()
    }
  }

  private selectFirstSwatch() {
    const attributeCode = this.loadedProduct().getSwatchAttribute()?.code
    if (! attributeCode) {
      return
    }

    const options = this.loadedProduct().selectableAttributes.get(attributeCode)?.options
    if (! options || ! options.length) {
      return
    }

    // select first possible swatch
    for (const option of options) {
      if (this.toggleActiveSelection(attributeCode, option, false)) {
        return
      }
    }
  }

  isAttributeValid(attributeCode: string | undefined): boolean {
    if (! attributeCode) {
      return false
    }

    return this.currentSelections.hasOwnProperty(attributeCode);
  }
}
